import {
  AddCircleOutline as AddCircleOutlineIcon,
  Email,
  Phone,
} from "@mui/icons-material"
import { Box, Chip, CircularProgress, Typography } from "@mui/material"
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { StyledButton } from "components/Button/StyledButton"
import { Customer } from "graphql/types" // Changed from Contact to Customer
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AppRoutes } from "store/routes" // Assuming AppRoutes will have a CUSTOMERS route

const Customers = () => {
  const navigate = useNavigate()
  const { customers } = useStoreState((state) => state) // Changed from contacts to customers
  const { fetchCustomers } = useStoreActions((actions) => actions) // Changed from fetchContacts to fetchCustomers
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchCustomers({ fresh: false }) // Changed from fetchContacts
      } catch (err) {
        console.error("Error loading customers:", err) // Changed message
        setError("Failed to load customers. Please try again later.") // Changed message
      } finally {
        setLoading(false)
      }
    }

    loadCustomers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Dependency array kept empty, assuming fetchCustomers is stable or memoized

  const handleRetry = async () => {
    try {
      setLoading(true)
      setError(null)
      await fetchCustomers({ fresh: true })
    } catch (err) {
      console.error("Error reloading customers:", err)
      setError("Failed to load customers. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  const columns: GridColDef<Customer>[] = [
    // Changed to Customer type
    {
      field: "id", // Using 'id' as it's the primary key in DataGrid
      headerName: "ID",
      flex: 0.5,
    },
    {
      field: "customerID",
      headerName: "Kunden-ID",
      flex: 1,
      renderCell: (params: GridRenderCellParams<Customer>) => (
        <Typography variant="body2">
          {params.row.customerID || "N/A"}
        </Typography>
      ),
    },
    {
      field: "customerName",
      headerName: "Name",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Customer>) => (
        <Typography variant="body2">
          {params.row.customerName || "N/A"}
        </Typography>
      ),
    },
    {
      field: "email",
      headerName: "E-Mail",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Customer>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.email && (
            <>
              <Email fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{params.row.email}</Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "phone1",
      headerName: "Telefon",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Customer>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.phone1 && (
            <>
              <Phone fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{params.row.phone1}</Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params: GridRenderCellParams<Customer>) => (
        <Chip
          label={params.row.status || "N/A"}
          color={
            params.row.status === "Active"
              ? "success"
              : params.row.status === "New" // Example, adjust as needed
                ? "primary"
                : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "country",
      headerName: "Land",
      flex: 1,
      renderCell: (params: GridRenderCellParams<Customer>) => (
        <Typography variant="body2">{params.row.country || "N/A"}</Typography>
      ),
    },
    {
      field: "city",
      headerName: "Stadt",
      flex: 1,
      renderCell: (params: GridRenderCellParams<Customer>) => (
        <Typography variant="body2">{params.row.city || "N/A"}</Typography>
      ),
    },
    // {
    //   field: "actions",
    //   type: "actions",
    //   headerName: "Actions",
    //   getActions: (params: GridRowParams<Customer>) => [
    //     <StyledButton
    //       key={`add-contact-${params.row.id}`}
    //       buttonType="custom"
    //       customIcon={<PersonAddAlt1Icon fontSize="small" />}
    //       onClick={() =>
    //         navigate(
    //           `${AppRoutes.CUSTOMERS}/${params.row.customerID}/create-contact`
    //         )
    //       }
    //       size="small"
    //       title="Create Contact for this Customer"
    //     >
    //       Add Contact
    //     </StyledButton>,
    //     <StyledButton
    //       key={`view-customer-${params.row.customerID}`}
    //       buttonType="custom"
    //       customIcon={<VisibilityIcon fontSize="small" />}
    //       onClick={() =>
    //         navigate(`${AppRoutes.CUSTOMERS}/${params.row.customerID}`)
    //       }
    //       size="small"
    //       title="View Customer Details"
    //     />,
    //   ],
    //   flex: 1,
    // },
  ]

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        {/* Changed to fetchCustomers */}
        <StyledButton
          onClick={handleRetry}
          sx={{ mt: 2 }}
        >
          Erneut versuchen
        </StyledButton>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Kunden
        </Typography>
        <StyledButton
          buttonType="custom"
          customIcon={<AddCircleOutlineIcon fontSize="small" />}
          onClick={() => navigate(AppRoutes.CREATE_CUSTOMER)}
          title="Neuen Kunden erstellen"
          disabled={true}
        >
          Neuen Kunden erstellen
        </StyledButton>
      </Box>
      <DataGrid
        rows={customers} // Changed from contacts
        columns={columns}
        autoHeight
        // getRowId={(row) => row.customerID || row.id} // Ensure unique ID if 'id' might not always be customerID
      />
    </Box>
  )
}

export default Customers
